import React, { useEffect, useMemo, useState } from 'react';
import { workstreamAPI, driversAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBritish, formatDateTimeBritish } from '../utils/dateUtils';
import './VehicleList.css';

interface PendingVehicle {
  id: number;
  registration: string;
  make: string;
  model: string;
  vehicleType: string;
  weightClass: string;
  year: string;
  vehicleValue: number;
  coverStatus: string;
  createdAt: string;
  clientId: number;
  clientName: string;
  siteName: string;
  instantCover?: boolean;
  registeredOwnerIsBusiness?: boolean | null;
  registeredOwnerName?: string | null;
  registeredKeeperIsBusiness?: boolean | null;
  registeredKeeperName?: string | null;
  hasModifications?: boolean;
  modificationDetails?: string | null;
  createdBy?: string;
  mtaValueThreshold?: number | null;
}

interface ReinstatementRequest {
  id: number;
  vehicleId: number;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  clientId: number;
  clientName: string;
  requestDate: string;
  requestedBy: string;
  requestNotes?: string;
  status: string;
}

interface RemovalRequest {
  id: number;
  vehicleId: number;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  clientId: number;
  clientName: string;
  requestDate: string;
  requestedBy: string;
  requestNotes?: string;
  coverEndDate?: string;
  status: string;
}

interface ReferredDriver {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  clientId: number;
  clientName?: string;
  createdAt: string;
  outstandingPoints?: number;
  penaltyCodes?: string;
  referralStatus: string;
  licenseNumber?: string;
  licenseExpiry?: string;
  licenceConsentGiven?: boolean;
  address?: string;
  restrictions?: string;
}

interface TempCoverRequest {
  id: number;
  vehicleId: number;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  clientId: number;
  clientName: string;
  coverStartDate: string;
  coverEndDate: string;
  requestDate: string;
  requestedBy: string;
  requestNotes?: string;
  status: string;
}

interface ChangeOfVehicleRequest {
  id: number;
  oldVehicleId: number;
  oldVehicleRegistration: string;
  oldVehicleMake: string;
  oldVehicleModel: string;
  clientId: number;
  clientName: string;
  newRegistration: string;
  newMake: string;
  newModel: string;
  newVehicleType?: string;
  newWeightClass?: string;
  newYear?: string;
  newVehicleValue?: string;
  coverStartDate: string;
  requestDate: string;
  requestedBy: string;
  requestNotes?: string;
  status: string;
}

interface DeclineDetails {
  declineReason: string;
  resolutionNotes: string;
  customerCalled: boolean;
}

const WorkstreamList: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const [activeTab, setActiveTab] = useState<'all' | 'vehicles' | 'tempCover' | 'changeVehicle' | 'reinstatements' | 'removals' | 'referredDrivers'>('all');
  const [currentItemType, setCurrentItemType] = useState<'vehicle' | 'tempCover' | 'changeVehicle' | 'reinstatement' | 'removal' | null>(null);
  const [vehicles, setVehicles] = useState<PendingVehicle[]>([]);
  const [reinstatementRequests, setReinstatementRequests] = useState<ReinstatementRequest[]>([]);
  const [removalRequests, setRemovalRequests] = useState<RemovalRequest[]>([]);
  const [tempCoverRequests, setTempCoverRequests] = useState<TempCoverRequest[]>([]);
  const [changeOfVehicleRequests, setChangeOfVehicleRequests] = useState<ChangeOfVehicleRequest[]>([]);
  const [referredDrivers, setReferredDrivers] = useState<ReferredDriver[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<'approve' | 'reject'>('approve');
  const [currentVehicleId, setCurrentVehicleId] = useState<number | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [coverEffectiveDate, setCoverEffectiveDate] = useState<string>('');
  const [clientRequestedRemovalDate, setClientRequestedRemovalDate] = useState<string | null>(null);
  const [pendingCoverSubmissionDate, setPendingCoverSubmissionDate] = useState<string | null>(null);
  const [recentActions, setRecentActions] = useState<any[]>([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [declineDetails, setDeclineDetails] = useState<DeclineDetails>({
    declineReason: '',
    resolutionNotes: '',
    customerCalled: false,
  });
  const [expandedDriverId, setExpandedDriverId] = useState<number | null>(null);
  const [expandedVehicleId, setExpandedVehicleId] = useState<number | null>(null);

  useEffect(() => {
    loadPendingVehicles();
    loadReinstatementRequests();
    loadRemovalRequests();
    loadTempCoverRequests();
    loadChangeOfVehicleRequests();
    loadRecentActions();
    loadReferredDrivers();
  }, []);

  const loadPendingVehicles = async () => {
    try {
      const response = await workstreamAPI.getPending();
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading pending vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReinstatementRequests = async () => {
    try {
      const response = await workstreamAPI.getReinstatementRequests();
      setReinstatementRequests(response.data);
    } catch (error) {
      console.error('Error loading reinstatement requests:', error);
    }
  };

  const loadRemovalRequests = async () => {
    try {
      const response = await workstreamAPI.getRemovalRequests();
      setRemovalRequests(response.data);
    } catch (error) {
      console.error('Error loading removal requests:', error);
    }
  };

  const loadTempCoverRequests = async () => {
    try {
      const response = await workstreamAPI.getTempCoverRequests();
      setTempCoverRequests(response.data);
    } catch (error) {
      console.error('Error loading temp cover requests:', error);
    }
  };

  const loadChangeOfVehicleRequests = async () => {
    try {
      const response = await workstreamAPI.getChangeOfVehicleRequests();
      setChangeOfVehicleRequests(response.data);
    } catch (error) {
      console.error('Error loading Change of Site Requests:', error);
    }
  };

  const loadRecentActions = async () => {
    try {
      const response = await workstreamAPI.getRecentActions();
      setRecentActions(response.data);
    } catch (error) {
      console.error('Error loading recent actions:', error);
    } finally {
      setLoadingActions(false);
    }
  };

  const loadReferredDrivers = async () => {
    try {
      const response = await driversAPI.getPendingReferrals();
      setReferredDrivers(response.data);
    } catch (error) {
      console.error('Error loading referred drivers:', error);
    }
  };

  const handleApproveReferral = async (id: number) => {
    if (!window.confirm('Approve this driver referral?')) return;
    try {
      await driversAPI.approveReferral(id);
      setReferredDrivers(prev => prev.filter(d => d.id !== id));
    } catch (error) {
      alert('Failed to approve referral');
    }
  };

  const handleRejectReferral = async (id: number) => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return;
    try {
      await driversAPI.rejectReferral(id, reason || undefined);
      setReferredDrivers(prev => prev.filter(d => d.id !== id));
    } catch (error) {
      alert('Failed to reject referral');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedVehicles(vehicles.map(v => v.id));
    } else {
      setSelectedVehicles([]);
    }
  };

  const handleSelectVehicle = (vehicleId: number) => {
    if (selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles(selectedVehicles.filter(id => id !== vehicleId));
    } else {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedVehicles.length === 0) {
      alert('Please select at least one vehicle to approve');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${selectedVehicles.length} vehicle(s)?`)) {
      return;
    }

    try {
      await workstreamAPI.bulkApprove(selectedVehicles);
      alert(`${selectedVehicles.length} vehicle(s) approved successfully`);
      setSelectedVehicles([]);
      loadPendingVehicles();
    } catch (error) {
      console.error('Error bulk approving vehicles:', error);
      alert('Failed to approve vehicles');
    }
  };

  const openNoteModal = (vehicleId: number, action: 'approve' | 'reject', submissionDate?: string) => {
    setCurrentVehicleId(vehicleId);
    setCurrentAction(action);
    setNote('');
    setCurrentItemType('vehicle');
    setCoverEffectiveDate(new Date().toISOString().split('T')[0]); // Default to today
    setPendingCoverSubmissionDate(submissionDate ?? null);
    if (action === 'reject') {
      setShowDeclineModal(true);
    } else {
      setShowNoteModal(true);
    }
  };

  const getItemTypeLabel = () => {
    switch (currentItemType) {
      case 'vehicle':       return 'Vehicle Cover Request';
      case 'tempCover':     return 'Temp Cover Request';
      case 'changeVehicle': return 'Change of Site Request';
      case 'reinstatement': return 'Reinstatement Request';
      case 'removal':       return 'Removal Request';
      default:              return 'Request';
    }
  };

  const handleSubmitAction = async () => {
    if (!currentVehicleId && !currentRequestId) return;
    setIsSubmitting(true);
    try {
      if (currentRequestId) {
        // Resolve effective tab (for All view, use currentItemType)
        const resolvedTab = activeTab === 'all'
          ? (currentItemType === 'removal' ? 'removals'
            : currentItemType === 'tempCover' ? 'tempCover'
            : currentItemType === 'changeVehicle' ? 'changeVehicle'
            : 'reinstatements')
          : activeTab;
        if (resolvedTab === 'removals') {
          // Processing removal request
          if (currentAction === 'approve') {
            await workstreamAPI.approveRemoval(currentRequestId, note, coverEffectiveDate);
            alert('Removal request approved successfully');
          } else {
            if (!note.trim()) {
              alert('Please provide a reason for rejection');
              return;
            }
            await workstreamAPI.rejectRemoval(currentRequestId, note);
            alert('Removal request rejected');
          }
          loadRemovalRequests();
        } else if (resolvedTab === 'tempCover') {
          // Processing temp cover request
          if (currentAction === 'approve') {
            await workstreamAPI.approveTempCover(currentRequestId, note);
            alert('Temp cover request approved successfully');
          } else {
            if (!note.trim()) {
              alert('Please provide a reason for rejection');
              return;
            }
            await workstreamAPI.rejectTempCover(currentRequestId, note);
            alert('Temp cover request rejected');
          }
          loadTempCoverRequests();
        } else if (resolvedTab === 'changeVehicle') {
          // Processing Change of Site Request
          if (currentAction === 'approve') {
            await workstreamAPI.approveChangeOfVehicle(currentRequestId, note);
            alert('Change of Site Request approved successfully');
          } else {
            if (!note.trim()) {
              alert('Please provide a reason for rejection');
              return;
            }
            await workstreamAPI.rejectChangeOfVehicle(currentRequestId, note);
            alert('Change of Site Request rejected');
          }
          loadChangeOfVehicleRequests();
        } else {
          // Processing reinstatement request
          if (currentAction === 'approve') {
            await workstreamAPI.approveReinstatement(currentRequestId, note, coverEffectiveDate);
            alert('Reinstatement request approved successfully');
          } else {
            if (!note.trim()) {
              alert('Please provide a reason for rejection');
              return;
            }
            await workstreamAPI.rejectReinstatement(currentRequestId, note);
            alert('Reinstatement request rejected');
          }
          loadReinstatementRequests();
        }
      } else if (currentVehicleId) {
        // Processing vehicle approval
        if (currentAction === 'approve') {
          await workstreamAPI.approve(currentVehicleId, note, coverEffectiveDate);
          alert('Vehicle approved successfully');
        } else {
          if (!note.trim()) {
            alert('Please provide a reason for rejection');
            return;
          }
          await workstreamAPI.reject(currentVehicleId, note);
          alert('Vehicle rejected successfully');
        }
        loadPendingVehicles();
      }
      setShowNoteModal(false);
      setNote('');
      setCurrentVehicleId(null);
      setCurrentRequestId(null);
      loadRecentActions();
    } catch (error) {
      console.error('Error processing request:', error);
      alert('Failed to process request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReinstatementModal = (requestId: number, action: 'approve' | 'reject', itemType?: 'tempCover' | 'changeVehicle' | 'reinstatement') => {
    setCurrentRequestId(requestId);
    setCurrentVehicleId(null);
    setCurrentAction(action);
    setNote('');
    setCoverEffectiveDate(new Date().toISOString().split('T')[0]); // Default to today
    if (itemType) setCurrentItemType(itemType);
    if (action === 'reject') {
      setShowDeclineModal(true);
    } else {
      setShowNoteModal(true);
    }
  };

  const openRemovalModal = (requestId: number, action: 'approve' | 'reject', coverEndDate?: string) => {
    setCurrentRequestId(requestId);
    setCurrentVehicleId(null);
    setCurrentAction(action);
    setNote('');
    setCurrentItemType('removal');
    if (action === 'approve') {
      // Pre-populate with client's requested removal date/time
      if (coverEndDate) {
        setClientRequestedRemovalDate(coverEndDate);
        // Convert ISO datetime to date-only for the date input
        setCoverEffectiveDate(coverEndDate.slice(0, 16));
      } else {
        setClientRequestedRemovalDate(null);
        setCoverEffectiveDate(new Date().toISOString().slice(0, 16));
      }
    }
    if (action === 'reject') {
      setShowDeclineModal(true);
    } else {
      setShowNoteModal(true);
    }
  };

  const handleDeclineSubmit = async () => {
    // Validation
    if (!declineDetails.declineReason.trim()) {
      alert('Please provide a reason for decline');
      return;
    }
    if (!declineDetails.resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }
    if (!declineDetails.customerCalled) {
      alert('Customer must be called before declining');
      return;
    }

    setIsSubmitting(true);
    try {
      if (currentRequestId) {
        const resolvedTab = activeTab === 'all'
          ? (currentItemType === 'removal' ? 'removals'
            : currentItemType === 'tempCover' ? 'tempCover'
            : currentItemType === 'changeVehicle' ? 'changeVehicle'
            : 'reinstatements')
          : activeTab;
        if (resolvedTab === 'removals') {
          await workstreamAPI.rejectRemoval(currentRequestId, undefined, declineDetails);
          alert('Removal request declined');
          loadRemovalRequests();
        } else if (resolvedTab === 'tempCover') {
          await workstreamAPI.rejectTempCover(currentRequestId, undefined, declineDetails);
          alert('Temp cover request declined');
          loadTempCoverRequests();
        } else if (resolvedTab === 'changeVehicle') {
          await workstreamAPI.rejectChangeOfVehicle(currentRequestId, undefined, declineDetails);
          alert('Change of Site Request declined');
          loadChangeOfVehicleRequests();
        } else {
          await workstreamAPI.rejectReinstatement(currentRequestId, undefined, declineDetails);
          alert('Reinstatement request declined');
          loadReinstatementRequests();
        }
      } else if (currentVehicleId) {
        await workstreamAPI.reject(currentVehicleId, '', declineDetails);
        alert('Vehicle declined');
        loadPendingVehicles();
      }
      setShowDeclineModal(false);
      setDeclineDetails({
        declineReason: '',
        resolutionNotes: '',
        customerCalled: false,
      });
      setCurrentVehicleId(null);
      setCurrentRequestId(null);
      loadRecentActions();
    } catch (error: any) {
      console.error('Error declining request:', error);
      alert(error.response?.data || 'Failed to decline request');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleSilentRemove = async (type: string, id: number) => {
    if (!window.confirm('Silently remove this item from the workstream? No email will be sent to the client.')) return;
    try {
      await workstreamAPI.silentRemoveItem(type, id);
      switch (type) {
        case 'vehicle':        loadPendingVehicles();          break;
        case 'reinstatement':  loadReinstatementRequests();    break;
        case 'removal':        loadRemovalRequests();           break;
        case 'tempcover':      loadTempCoverRequests();         break;
        case 'changeofvehicle': loadChangeOfVehicleRequests(); break;
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove item');
    }
  };

  const allItems = useMemo(() => {
    const items = [
      ...vehicles.map(v => ({
        id: v.id,
        type: 'vehicle' as const,
        registration: v.registration,
        makeModel: `${v.make} ${v.model}`,
        clientName: v.clientName,
        requestDate: v.createdAt,
        requestedBy: '—',
        notes: '',
        instantCover: v.instantCover ?? false,
        pendFlags: {
          individualOwner: v.registeredOwnerIsBusiness === false || v.registeredKeeperIsBusiness === false,
          ownerName: v.registeredOwnerIsBusiness === false
            ? (v.registeredOwnerName || 'Individual')
            : (v.registeredKeeperIsBusiness === false ? (v.registeredKeeperName || 'Individual') : null),
          highValue: v.mtaValueThreshold != null && v.vehicleValue >= v.mtaValueThreshold,
          threshold: v.mtaValueThreshold,
          hasModifications: v.hasModifications ?? false,
          modificationDetails: v.modificationDetails ?? null,
          createdBy: v.createdBy,
          vehicleValue: v.vehicleValue,
        },
      })),
      ...tempCoverRequests.map(r => ({
        id: r.id,
        type: 'tempCover' as const,
        registration: r.vehicleRegistration,
        makeModel: `${r.vehicleMake} ${r.vehicleModel}`,
        clientName: r.clientName,
        requestDate: r.requestDate,
        requestedBy: r.requestedBy,
        notes: r.requestNotes ?? '',
        instantCover: false,
      })),
      ...changeOfVehicleRequests.map(r => ({
        id: r.id,
        type: 'changeVehicle' as const,
        registration: r.oldVehicleRegistration,
        makeModel: `${r.oldVehicleMake} ${r.oldVehicleModel}`,
        clientName: r.clientName,
        requestDate: r.requestDate,
        requestedBy: r.requestedBy,
        notes: r.requestNotes ?? '',
        instantCover: false,
      })),
      ...reinstatementRequests.map(r => ({
        id: r.id,
        type: 'reinstatement' as const,
        registration: r.vehicleRegistration,
        makeModel: `${r.vehicleMake} ${r.vehicleModel}`,
        clientName: r.clientName,
        requestDate: r.requestDate,
        requestedBy: r.requestedBy,
        notes: r.requestNotes ?? '',
        instantCover: false,
      })),
      ...removalRequests.map(r => ({
        id: r.id,
        type: 'removal' as const,
        registration: r.vehicleRegistration,
        makeModel: `${r.vehicleMake} ${r.vehicleModel}`,
        clientName: r.clientName,
        requestDate: r.requestDate,
        requestedBy: r.requestedBy,
        notes: r.requestNotes ?? '',
        instantCover: false,
      })),
      ...referredDrivers.map(d => ({
        id: d.id,
        type: 'referredDriver' as const,
        registration: `${d.firstName} ${d.lastName}`,
        makeModel: [
          d.outstandingPoints != null ? `${d.outstandingPoints} pts` : null,
          d.penaltyCodes || null,
        ].filter(Boolean).join(' · ') || '—',
        clientName: (d as any).client?.companyName || d.clientName || '—',
        requestDate: d.createdAt,
        requestedBy: 'Driver Referral',
        notes: '',
        instantCover: false,
      })),
    ];
    return items.sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
  }, [vehicles, tempCoverRequests, changeOfVehicleRequests, reinstatementRequests, removalRequests, referredDrivers]);

  if (loading) {
    return <div>Loading pending vehicles...</div>;
  }

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Workstream</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('all')}
          >
            All ({allItems.length})
          </button>
          <button 
            className={`btn ${activeTab === 'vehicles' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('vehicles')}
          >
            Pending Cover ({vehicles.length})
          </button>
          <button 
            className={`btn ${activeTab === 'tempCover' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('tempCover')}
          >
            Temp Cover ({tempCoverRequests.length})
          </button>
          <button 
            className={`btn ${activeTab === 'changeVehicle' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('changeVehicle')}
          >
            Change of Site ({changeOfVehicleRequests.length})
          </button>
          <button 
            className={`btn ${activeTab === 'reinstatements' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('reinstatements')}
          >
            Reinstatements ({reinstatementRequests.length})
          </button>
          <button 
            className={`btn ${activeTab === 'removals' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('removals')}
          >
            Removals ({removalRequests.length})
          </button>
          <button
            className={`btn ${activeTab === 'referredDrivers' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('referredDrivers')}
          >
            Referred Drivers ({referredDrivers.length})
          </button>
          {activeTab === 'vehicles' && selectedVehicles.length > 0 && (
            <button 
              className="btn btn-success" 
              onClick={handleBulkApprove}
            >
              ✓ Approve Selected ({selectedVehicles.length})
            </button>
          )}
        </div>
      </div>

      {showNoteModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Coloured accent bar */}
            <div style={{
              height: '5px',
              background: currentAction === 'approve' ? 'linear-gradient(90deg, #16a34a, #22c55e)' : 'linear-gradient(90deg, #dc2626, #ef4444)',
              borderRadius: '12px 12px 0 0'
            }} />
            <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: currentAction === 'approve' ? '#dcfce7' : '#fee2e2',
                  color: currentAction === 'approve' ? '#16a34a' : '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 700, flexShrink: 0
                }}>
                  {currentAction === 'approve' ? '✓' : '✕'}
                </span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a202c' }}>
                    {currentAction === 'approve' ? 'Approve' : 'Reject'} {getItemTypeLabel()}
                  </h2>
                </div>
              </div>
              <button className="close-btn" onClick={() => { if (!isSubmitting) setShowNoteModal(false); }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              {currentAction === 'approve' && (
                <div className="form-group">
                  {currentItemType === 'removal' && clientRequestedRemovalDate && (
                    <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13, color: '#0c4a6e' }}>
                      <strong>Client requested removal:</strong> {formatDateTimeBritish(clientRequestedRemovalDate)}
                    </div>
                  )}
                  {currentItemType === 'vehicle' && pendingCoverSubmissionDate && (
                    <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13, color: '#0c4a6e' }}>
                      <strong>Date submitted for cover:</strong> {formatDateBritish(pendingCoverSubmissionDate)}
                    </div>
                  )}
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
                    {currentItemType === 'removal' ? 'Date & Time Cover Ceases *' : 'Cover Effective Date *'}
                  </label>
                  <input
                    type={currentItemType === 'removal' ? 'datetime-local' : 'date'}
                    value={coverEffectiveDate}
                    onChange={(e) => setCoverEffectiveDate(e.target.value)}
                    disabled={isSubmitting}
                    required
                    style={{ width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1.5px solid #d1d5db', boxSizing: 'border-box' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {currentItemType === 'removal'
                      ? 'Select the exact date and time this vehicle\'s cover will cease.'
                      : 'Future dates will show as "Cover Pending" until that date is reached.'}
                  </small>
                </div>
              )}

              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
                  {currentAction === 'approve' ? 'Notes (Optional)' : 'Reason for Rejection *'}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={currentAction === 'approve' ? 'Add any notes for the client...' : 'Please explain why this request is being rejected...'}
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1.5px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box' }}
                  required={currentAction === 'reject'}
                />
              </div>

              <div className="form-actions" style={{ marginTop: 0, paddingTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNoteModal(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${currentAction === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                  onClick={handleSubmitAction}
                  disabled={isSubmitting}
                  style={{ minWidth: 160 }}
                >
                  {isSubmitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                      <span className="ws-spinner" /> Sending notification...
                    </span>
                  ) : (
                    `${currentAction === 'approve' ? '✓ Approve' : '✕ Reject'} ${getItemTypeLabel()}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeclineModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Red accent bar */}
            <div style={{ height: '5px', background: 'linear-gradient(90deg, #dc2626, #ef4444)', borderRadius: '12px 12px 0 0' }} />
            <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: '#fee2e2', color: '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 700, flexShrink: 0
                }}>✕</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a202c' }}>Decline {getItemTypeLabel()}</h2>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>⚠ Customer must be called before declining</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => { if (!isSubmitting) setShowDeclineModal(false); }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>Reason for Decline *</label>
                <textarea
                  value={declineDetails.declineReason}
                  onChange={(e) => setDeclineDetails({...declineDetails, declineReason: e.target.value})}
                  disabled={isSubmitting}
                  placeholder="Explain why this is being declined..."
                  rows={3}
                  maxLength={500}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1.5px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {declineDetails.declineReason.length}/500
                </small>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>Resolution / Notes *</label>
                <textarea
                  value={declineDetails.resolutionNotes}
                  onChange={(e) => setDeclineDetails({...declineDetails, resolutionNotes: e.target.value})}
                  disabled={isSubmitting}
                  placeholder="Provide resolution notes or follow-up actions..."
                  rows={4}
                  maxLength={1000}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1.5px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {declineDetails.resolutionNotes.length}/1000
                </small>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>Have We Called the Customer? *</label>
                <select
                  value={declineDetails.customerCalled ? 'yes' : 'no'}
                  onChange={(e) => setDeclineDetails({...declineDetails, customerCalled: e.target.value === 'yes'})}
                  disabled={isSubmitting}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1.5px solid #d1d5db', background: '#fff' }}
                >
                  <option value="no">No — not yet</option>
                  <option value="yes">Yes — customer has been called</option>
                </select>
              </div>

              <div className="form-actions" style={{ marginTop: 0, paddingTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeclineModal(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeclineSubmit}
                  disabled={!declineDetails.customerCalled || isSubmitting}
                  style={{ minWidth: 160 }}
                >
                  {isSubmitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                      <span className="ws-spinner" /> Sending notification...
                    </span>
                  ) : (
                    '✕ Confirm Decline'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'vehicles' ? (
        <div className="card">
          {vehicles.length === 0 ? (
            <p className="empty-state">No pending vehicles. All vehicles have been reviewed.</p>
          ) : (
            <table>
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox"
                    checked={selectedVehicles.length === vehicles.length && vehicles.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Registration</th>
                <th>Make & Model</th>
                <th>Type</th>
                <th>Weight Class</th>
                <th>Year</th>
                <th>Value</th>
                <th>Flags</th>
                <th>Client</th>
                <th>Site</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
            {vehicles.map((vehicle) => (
                <React.Fragment key={vehicle.id}>
                <tr>
                  <td>
                    <input 
                      type="checkbox"
                      checked={selectedVehicles.includes(vehicle.id)}
                      onChange={() => handleSelectVehicle(vehicle.id)}
                    />
                  </td>
                  <td><strong>{vehicle.registration}</strong></td>
                  <td>{vehicle.make} {vehicle.model}</td>
                  <td>{vehicle.vehicleType}</td>
                  <td>{vehicle.weightClass}</td>
                  <td>{vehicle.year}</td>
                  <td>£{vehicle.vehicleValue?.toLocaleString() || '0'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {vehicle.hasModifications && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                          <span className="pend-orb" />
                          Modifications
                        </span>
                      )}
                      {(vehicle.registeredOwnerIsBusiness === false || vehicle.registeredKeeperIsBusiness === false) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                          <span className="pend-orb" />
                          Individual Owner
                        </span>
                      )}
                      {vehicle.mtaValueThreshold != null && vehicle.vehicleValue >= vehicle.mtaValueThreshold && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                          <span className="pend-orb" />
                          High Value
                        </span>
                      )}
                      {!vehicle.hasModifications && vehicle.registeredOwnerIsBusiness !== false && vehicle.registeredKeeperIsBusiness !== false && (vehicle.mtaValueThreshold == null || vehicle.vehicleValue < vehicle.mtaValueThreshold) && (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                      )}
                    </div>
                  </td>
                  <td>{vehicle.clientName}</td>
                  <td>{vehicle.siteName}</td>
                  <td>{formatDateBritish(vehicle.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => setExpandedVehicleId(expandedVehicleId === vehicle.id ? null : vehicle.id)}
                      style={{ display: 'block', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap', marginBottom: 6 }}
                    >
                      {expandedVehicleId === vehicle.id ? '▲ Hide Detail' : '▼ Pend Detail'}
                    </button>
                    <div className="action-buttons" style={{gap: '8px'}}>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => openNoteModal(vehicle.id, 'approve')}
                        style={{padding: '4px 12px', fontSize: '13px'}}
                      >
                        ✓ Approve
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => openNoteModal(vehicle.id, 'reject')}
                        style={{padding: '4px 12px', fontSize: '13px'}}
                      >
                        ✕ Reject
                      </button>
                      {isSuperAdmin && (
                        <button
                          title="Silent Remove (no email)"
                          onClick={() => handleSilentRemove('vehicle', vehicle.id)}
                          style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                        >
                          🔇
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedVehicleId === vehicle.id && (
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <td colSpan={12} style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px', fontSize: 13 }}>
                        {vehicle.createdBy && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Submitted By</div>
                            <div style={{ color: '#1e293b' }}>{vehicle.createdBy}</div>
                          </div>
                        )}
                        {vehicle.hasModifications ? (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>⚙️ Modifications</div>
                            <div style={{ color: '#1e293b', maxWidth: 340 }}>{vehicle.modificationDetails || 'No details provided'}</div>
                          </div>
                        ) : null}
                        {(vehicle.registeredOwnerIsBusiness === false || vehicle.registeredKeeperIsBusiness === false) ? (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>👤 Non-Business Owner/Keeper</div>
                            <div style={{ color: '#1e293b' }}>
                              {vehicle.registeredOwnerIsBusiness === false && <div>Owner: {vehicle.registeredOwnerName || 'Individual'}</div>}
                              {vehicle.registeredKeeperIsBusiness === false && <div>Keeper: {vehicle.registeredKeeperName || 'Individual'}</div>}
                            </div>
                          </div>
                        ) : null}
                        {vehicle.mtaValueThreshold != null && vehicle.vehicleValue >= vehicle.mtaValueThreshold ? (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>💰 High Declared Value</div>
                            <div style={{ color: '#1e293b' }}>£{vehicle.vehicleValue?.toLocaleString()} declared (threshold: £{vehicle.mtaValueThreshold?.toLocaleString()})</div>
                          </div>
                        ) : null}
                        {!vehicle.hasModifications && vehicle.registeredOwnerIsBusiness !== false && vehicle.registeredKeeperIsBusiness !== false && (vehicle.mtaValueThreshold == null || vehicle.vehicleValue < vehicle.mtaValueThreshold) && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Pend Reason</div>
                            <div style={{ color: '#6b7280', fontStyle: 'italic' }}>Standard review — no specific flags</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      ) : activeTab === 'tempCover' ? (
        <div className="card">
          {tempCoverRequests.length === 0 ? (
            <p className="empty-state">No pending temp cover requests.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Client</th>
                  <th>Cover Period</th>
                  <th>Requested By</th>
                  <th>Request Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tempCoverRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong>{request.vehicleRegistration}</strong><br />
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {request.vehicleMake} {request.vehicleModel}
                      </span>
                    </td>
                    <td>{request.clientName}</td>
                    <td>
                      <div style={{ fontSize: '13px' }}>
                        <div>{formatDateBritish(request.coverStartDate)}</div>
                        <div style={{ color: '#718096', fontSize: '11px' }}>to</div>
                        <div>{formatDateBritish(request.coverEndDate)}</div>
                      </div>
                    </td>
                    <td>{request.requestedBy}</td>
                    <td>{formatDateBritish(request.requestDate)}</td>
                    <td>
                      {request.requestNotes || <span style={{ color: '#718096', fontStyle: 'italic' }}>No notes</span>}
                    </td>
                    <td>
                      <div className="action-buttons" style={{gap: '8px'}}>
                        <button 
                          className="btn btn-primary" 
                          onClick={() => openReinstatementModal(request.id, 'approve')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✓ Approve
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => openReinstatementModal(request.id, 'reject')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✕ Reject
                        </button>
                        {isSuperAdmin && (
                          <button
                            title="Silent Remove (no email)"
                            onClick={() => handleSilentRemove('tempcover', request.id)}
                            style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                          >
                            🔇
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeTab === 'changeVehicle' ? (
        <div className="card">
          {changeOfVehicleRequests.length === 0 ? (
            <p className="empty-state">No pending Change of Site Requests.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Old Vehicle</th>
                  <th>New Vehicle</th>
                  <th>Client</th>
                  <th>Cover Start</th>
                  <th>Requested By</th>
                  <th>Request Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {changeOfVehicleRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong>{request.oldVehicleRegistration}</strong><br />
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {request.oldVehicleMake} {request.oldVehicleModel}
                      </span>
                    </td>
                    <td>
                      <strong>{request.newRegistration}</strong><br />
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {request.newMake} {request.newModel}
                      </span>
                    </td>
                    <td>{request.clientName}</td>
                    <td>{formatDateBritish(request.coverStartDate)}</td>
                    <td>{request.requestedBy}</td>
                    <td>{formatDateBritish(request.requestDate)}</td>
                    <td>
                      {request.requestNotes || <span style={{ color: '#718096', fontStyle: 'italic' }}>No notes</span>}
                    </td>
                    <td>
                      <div className="action-buttons" style={{gap: '8px'}}>
                        <button 
                          className="btn btn-primary" 
                          onClick={() => openReinstatementModal(request.id, 'approve')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✓ Approve
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => openReinstatementModal(request.id, 'reject')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✕ Reject
                        </button>
                        {isSuperAdmin && (
                          <button
                            title="Silent Remove (no email)"
                            onClick={() => handleSilentRemove('changeofvehicle', request.id)}
                            style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                          >
                            🔇
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeTab === 'reinstatements' ? (
        <div className="card">
          {reinstatementRequests.length === 0 ? (
            <p className="empty-state">No pending reinstatement requests.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Client</th>
                  <th>Requested By</th>
                  <th>Request Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reinstatementRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong>{request.vehicleRegistration}</strong><br />
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {request.vehicleMake} {request.vehicleModel}
                      </span>
                    </td>
                    <td>{request.clientName}</td>
                    <td>{request.requestedBy}</td>
                    <td>{formatDateBritish(request.requestDate)}</td>
                    <td>
                      {request.requestNotes || <span style={{ color: '#718096', fontStyle: 'italic' }}>No notes</span>}
                    </td>
                    <td>
                      <div className="action-buttons" style={{gap: '8px'}}>
                        <button 
                          className="btn btn-primary" 
                          onClick={() => openReinstatementModal(request.id, 'approve')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✓ Approve
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => openReinstatementModal(request.id, 'reject')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✕ Reject
                        </button>
                        {isSuperAdmin && (
                          <button
                            title="Silent Remove (no email)"
                            onClick={() => handleSilentRemove('reinstatement', request.id)}
                            style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                          >
                            🔇
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeTab === 'removals' ? (
        <div className="card">
          {removalRequests.length === 0 ? (
            <p className="empty-state">No pending removal requests.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Client</th>
          <th>Requested By</th>
                  <th>Request Date</th>
                  <th>Requested Cover End</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {removalRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong>{request.vehicleRegistration}</strong><br />
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {request.vehicleMake} {request.vehicleModel}
                      </span>
                    </td>
                    <td>{request.clientName}</td>
                    <td>{request.requestedBy}</td>
                    <td>{formatDateBritish(request.requestDate)}</td>
                    <td style={{ fontSize: 13 }}>
                      {request.coverEndDate ? formatDateTimeBritish(request.coverEndDate) : <span style={{ color: '#718096', fontStyle: 'italic' }}>Not specified</span>}
                    </td>
                    <td>
                      {request.requestNotes || <span style={{ color: '#718096', fontStyle: 'italic' }}>No notes</span>}
                    </td>
                    <td>
                      <div className="action-buttons" style={{gap: '8px'}}>
                        <button 
                          className="btn btn-primary" 
                          onClick={() => openRemovalModal(request.id, 'approve', request.coverEndDate)}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✓ Approve
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => openRemovalModal(request.id, 'reject')}
                          style={{padding: '4px 12px', fontSize: '13px'}}
                        >
                          ✕ Reject
                        </button>
                        {isSuperAdmin && (
                          <button
                            title="Silent Remove (no email)"
                            onClick={() => handleSilentRemove('removal', request.id)}
                            style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                          >
                            🔇
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeTab === 'referredDrivers' ? (
        <div className="card">
          {referredDrivers.length === 0 ? (
            <p className="empty-state">No pending referred drivers awaiting approval.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Outstanding Points</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {referredDrivers.map((driver) => (
                  <React.Fragment key={driver.id}>
                    <tr>
                      <td><strong>{driver.firstName} {driver.lastName}</strong></td>
                      <td>{(driver as any).client?.companyName || '—'}</td>
                      <td style={{ fontSize: 13 }}>{driver.email || '—'}</td>
                      <td style={{ fontSize: 13 }}>{driver.phone || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {driver.outstandingPoints != null ? (
                          <span style={{ fontWeight: 600, color: driver.outstandingPoints > 0 ? '#b45309' : '#166534' }}>
                            {driver.outstandingPoints}
                          </span>
                        ) : '—'}
                        {driver.penaltyCodes && (
                          <span style={{ marginLeft: 6, fontSize: 12, color: '#6b7280' }}>({driver.penaltyCodes})</span>
                        )}
                      </td>
                      <td style={{ fontSize: 13 }}>{formatDateBritish(driver.createdAt)}</td>
                      <td>
                        <button
                          onClick={() => setExpandedDriverId(expandedDriverId === driver.id ? null : driver.id)}
                          style={{ display: 'block', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap', marginBottom: 6, textAlign: 'left' }}
                        >
                          {expandedDriverId === driver.id ? '▲ Hide Details' : '▼ See Referred Driver Detail'}
                        </button>
                        <div className="action-buttons" style={{ gap: '8px' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleApproveReferral(driver.id)}
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleRejectReferral(driver.id)}
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                          >
                            ✕ Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedDriverId === driver.id && (
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <td colSpan={7} style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 24px', fontSize: 13 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Number</div>
                              <div style={{ fontFamily: 'monospace', color: '#1e293b' }}>{driver.licenseNumber || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not recorded</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Expiry</div>
                              <div>{driver.licenseExpiry ? formatDateBritish(driver.licenseExpiry) : <span style={{ color: '#9ca3af' }}>—</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Penalty Codes</div>
                              <div>{driver.penaltyCodes || <span style={{ color: '#9ca3af' }}>None declared</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Address</div>
                              <div style={{ whiteSpace: 'pre-line' }}>{driver.address || <span style={{ color: '#9ca3af' }}>—</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Restrictions</div>
                              <div>{driver.restrictions || <span style={{ color: '#9ca3af' }}>None</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Consent Given</div>
                              <div style={{ color: driver.licenceConsentGiven ? '#15803d' : '#9ca3af' }}>
                                {driver.licenceConsentGiven ? '✓ Data consent recorded' : '— Not given'}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // All items — unified sorted view
        <div className="card">
          {allItems.length === 0 ? (
            <p className="empty-state">No pending items across all categories.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Registration</th>
                  <th>Client</th>
                  <th>Requested By</th>
                  <th>Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((item) => {
                  const typeMeta: Record<string, { label: string; bg: string; color: string }> = {
                    vehicle:       { label: 'Pending Cover',      bg: '#dbeafe', color: '#1e40af' },
                    tempCover:     { label: 'Temp Cover',          bg: '#ccfbf1', color: '#0f766e' },
                    changeVehicle: { label: 'Change of Site',   bg: '#ede9fe', color: '#6d28d9' },
                    reinstatement: { label: 'Reinstatement',       bg: '#fef3c7', color: '#92400e' },
                    removal:        { label: 'Removal',            bg: '#fee2e2', color: '#b91c1c' },
                    referredDriver: { label: 'Referred Driver',    bg: '#fef3c7', color: '#b45309' },
                  };
                  const meta = typeMeta[item.type] ?? { label: item.type, bg: '#f3f4f6', color: '#374151' };
                  const driverDetail = item.type === 'referredDriver' ? referredDrivers.find(d => d.id === item.id) : null;
                  return (
                    <React.Fragment key={`${item.type}-${item.id}`}>
                    <tr>
                      <td>
                        <span style={{ background: meta.bg, color: meta.color, padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {meta.label}
                        </span>
                      </td>
                      <td>
                        <strong>{item.registration}</strong>
                        {item.type === 'vehicle' && item.instantCover && (
                          <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>
                            ⚡ Instant Cover
                          </span>
                        )}
                        <br />
                        <span style={{ fontSize: 12, color: '#718096' }}>{item.makeModel}</span>
                        {item.type === 'vehicle' && (
                          (item as any).pendFlags?.hasModifications ||
                          (item as any).pendFlags?.individualOwner ||
                          (item as any).pendFlags?.highValue
                        ) && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                            {(item as any).pendFlags?.hasModifications && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#b91c1c', padding: '1px 7px', borderRadius: 9999, fontSize: 10, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                                <span className="pend-orb" />
                                Mods
                              </span>
                            )}
                            {(item as any).pendFlags?.individualOwner && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#b91c1c', padding: '1px 7px', borderRadius: 9999, fontSize: 10, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                                <span className="pend-orb" />
                                Ind. Owner
                              </span>
                            )}
                            {(item as any).pendFlags?.highValue && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#b91c1c', padding: '1px 7px', borderRadius: 9999, fontSize: 10, fontWeight: 600, border: '1px solid #fca5a5', whiteSpace: 'nowrap' }}>
                                <span className="pend-orb" />
                                High Value
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{item.clientName}</td>
                      <td style={{ fontSize: 13 }}>{item.requestedBy}</td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDateBritish(item.requestDate)}</td>
                      <td style={{ fontSize: 13 }}>
                        {item.notes || <span style={{ color: '#718096', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td>
                        {item.type === 'vehicle' && (
                          <button
                            onClick={() => setExpandedVehicleId(expandedVehicleId === item.id ? null : item.id)}
                            style={{ display: 'block', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap', marginBottom: 6 }}
                          >
                            {expandedVehicleId === item.id ? '▲ Hide Detail' : '▼ Pend Detail'}
                          </button>
                        )}
                        {item.type === 'referredDriver' && (
                          <button
                            onClick={() => setExpandedDriverId(expandedDriverId === item.id ? null : item.id)}
                            style={{ display: 'block', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap', marginBottom: 6 }}
                          >
                            {expandedDriverId === item.id ? '▲ Hide Details' : '▼ See Referred Driver Detail'}
                          </button>
                        )}
                        <div className="action-buttons" style={{ gap: '8px' }}>
                          <button
                            className="btn btn-primary"
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                            onClick={() => {
                              if (item.type === 'referredDriver') {
                                handleApproveReferral(item.id);
                            } else if (item.type === 'vehicle') {
                                openNoteModal(item.id, 'approve', item.requestDate);
                              } else if (item.type === 'removal') {
                                const req = removalRequests.find(r => r.id === item.id);
                                openRemovalModal(item.id, 'approve', req?.coverEndDate);
                              } else {
                                openReinstatementModal(item.id, 'approve', item.type as 'tempCover' | 'changeVehicle' | 'reinstatement');
                              }
                            }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                            onClick={() => {
                              if (item.type === 'referredDriver') {
                                handleRejectReferral(item.id);
                              } else if (item.type === 'vehicle') {
                                openNoteModal(item.id, 'reject');
                              } else if (item.type === 'removal') {
                                openRemovalModal(item.id, 'reject');
                              } else {
                                openReinstatementModal(item.id, 'reject', item.type as 'tempCover' | 'changeVehicle' | 'reinstatement');
                              }
                            }}
                          >
                            ✕ Reject
                          </button>
                          {isSuperAdmin && item.type !== 'referredDriver' && (
                            <button
                              title="Silent Remove (no email)"
                              onClick={() => {
                                const typeMap: Record<string, string> = {
                                  vehicle: 'vehicle',
                                  tempCover: 'tempcover',
                                  changeVehicle: 'changeofvehicle',
                                  reinstatement: 'reinstatement',
                                  removal: 'removal',
                                };
                                handleSilentRemove(typeMap[item.type] ?? item.type, item.id);
                              }}
                              style={{ padding: '4px 8px', fontSize: '13px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}
                            >
                              🔇
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {item.type === 'vehicle' && expandedVehicleId === item.id && (() => {
                      const pf = (item as any).pendFlags;
                      return (
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                          <td colSpan={7} style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px', fontSize: 13 }}>
                              {pf?.createdBy && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Submitted By</div>
                                  <div style={{ color: '#1e293b' }}>{pf.createdBy}</div>
                                </div>
                              )}
                              {pf?.hasModifications ? (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>⚙️ Modifications</div>
                                  <div style={{ color: '#1e293b', maxWidth: 300 }}>{pf.modificationDetails || 'No details provided'}</div>
                                </div>
                              ) : null}
                              {pf?.individualOwner ? (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>👤 Non-Business Owner/Keeper</div>
                                  <div style={{ color: '#1e293b' }}>{pf.ownerName || 'Individual'}</div>
                                </div>
                              ) : null}
                              {pf?.highValue ? (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>💰 High Declared Value</div>
                                  <div style={{ color: '#1e293b' }}>£{pf.vehicleValue?.toLocaleString()} declared (threshold: £{pf.threshold?.toLocaleString()})</div>
                                </div>
                              ) : null}
                              {!pf?.hasModifications && !pf?.individualOwner && !pf?.highValue && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Pend Reason</div>
                                  <div style={{ color: '#6b7280', fontStyle: 'italic' }}>Standard review — no specific flags</div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                    {item.type === 'referredDriver' && expandedDriverId === item.id && driverDetail && (
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <td colSpan={7} style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 24px', fontSize: 13 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Number</div>
                              <div style={{ fontFamily: 'monospace', color: '#1e293b' }}>{driverDetail.licenseNumber || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not recorded</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Expiry</div>
                              <div>{driverDetail.licenseExpiry ? formatDateBritish(driverDetail.licenseExpiry) : <span style={{ color: '#9ca3af' }}>—</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Penalty Codes</div>
                              <div>{driverDetail.penaltyCodes || <span style={{ color: '#9ca3af' }}>None declared</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Address</div>
                              <div style={{ whiteSpace: 'pre-line' }}>{driverDetail.address || <span style={{ color: '#9ca3af' }}>—</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Licence Restrictions</div>
                              <div>{driverDetail.restrictions || <span style={{ color: '#9ca3af' }}>None</span>}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Consent Given</div>
                              <div style={{ color: driverDetail.licenceConsentGiven ? '#15803d' : '#9ca3af' }}>
                                {driverDetail.licenceConsentGiven ? '✓ Data consent recorded' : '— Not given'}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{marginTop: '32px'}}>
        <h2 style={{marginBottom: '16px'}}>Recent Actions (Last 30 Days)</h2>
        {loadingActions ? (
          <p>Loading recent actions...</p>
        ) : recentActions.length === 0 ? (
          <p className="empty-state">No recent actions to display.</p>
        ) : (
          <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', backgroundColor: '#fff'}}>
            {recentActions.map((action) => (
              <div key={action.id} style={{padding: '12px', borderBottom: '1px solid #f0f0f0', marginBottom: '8px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                      <span style={{
                        backgroundColor: action.action === 'Approved' ? '#28a745' : 
                                       action.action === 'Reinstated' ? '#17a2b8' :
                                       action.action === 'Removed from Cover' ? '#dc3545' : 
                                       action.action === 'Rejected' ? '#ffc107' : '#6c757d',
                        color: 'white',
                        padding: '3px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {action.action}
                      </span>
                      <strong style={{fontSize: '14px'}}>{action.vehicleRegistration}</strong>
                      <span style={{color: '#718096', fontSize: '13px'}}>
                        {action.vehicleMake} {action.vehicleModel}
                      </span>
                    </div>
                    {action.note && (
                      <p style={{margin: '6px 0', fontSize: '13px', color: '#4a5568', paddingLeft: '4px'}}>
                        {action.note}
                      </p>
                    )}
                    <p style={{margin: '4px 0', fontSize: '12px', color: '#718096', paddingLeft: '4px'}}>
                      {formatDateTimeBritish(action.timestamp)}
                    </p>
                  </div>
                  <div style={{textAlign: 'right', minWidth: '140px', paddingLeft: '16px'}}>
                    <p style={{fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '4px'}}>
                      {action.adminName}
                    </p>
                    <p style={{fontSize: '11px', color: '#28a745', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px'}}>
                      <span style={{fontSize: '14px'}}>✓</span> Actioned
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkstreamList;
